import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Per-device UI preferences for the Lnp edit table.
 *
 * Persisted to localStorage (key: `lnp-edit-preferences`) so column visibility
 * + page size survive across navigation and reloads. Edit-state (dirty cells,
 * version locks) lives in `useEditStore` and is intentionally NOT persisted —
 * stale dirty state across sessions would mislead the user.
 */

export const DEFAULT_VISIBLE_LNP_COLUMNS: string[] = [
  "index",
  "lnp_id",
  "experiment_id",
  "il_name",
  "il_smiles",
  "hl_name",
  "chl_name",
  "peg_name",
  "il_molratio",
  "experiment_method",
  "experiment_value",
  "version",
];

export const DEFAULT_PAGE_SIZE = 10;

interface EditPreferenceState {
  visibleColumns: string[];
  pageSize: number;
  setVisibleColumns: (names: string[]) => void;
  toggleColumn: (name: string) => void;
  setPageSize: (n: number) => void;
  resetPreferences: () => void;
}

const useEditPreferenceStore = create<EditPreferenceState>()(
  persist(
    (set) => ({
      visibleColumns: DEFAULT_VISIBLE_LNP_COLUMNS,
      pageSize: DEFAULT_PAGE_SIZE,

      setVisibleColumns: (names) => set({ visibleColumns: names }),

      toggleColumn: (name) =>
        set((state) => {
          const has = state.visibleColumns.includes(name);
          return {
            visibleColumns: has
              ? state.visibleColumns.filter((n) => n !== name)
              : [...state.visibleColumns, name],
          };
        }),

      setPageSize: (n) => set({ pageSize: n }),

      resetPreferences: () =>
        set({
          visibleColumns: DEFAULT_VISIBLE_LNP_COLUMNS,
          pageSize: DEFAULT_PAGE_SIZE,
        }),
    }),
    {
      name: "lnp-edit-preferences",
      version: 1,
    }
  )
);

export default useEditPreferenceStore;
